import React, { useContext } from 'react';
import proptypes from 'prop-types';
import { useImpression, ImpressionProvider } from '@thd-olt-component-react/impression';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import Responsive from 'react-responsive';
import { WelcomeCardContext } from '../../contexts';
import { ComponentProviders } from '../../core/ComponentProviders';
import { LoyaltyWelcomeCardModel } from '../../common/models/UserInfoModels';
import { SpendSummary } from '../LoyaltyWelcomeCard/SpendSummary/SpendSummary';
import { OffersSkinnyBarNotifications } from './OffersNotificationSection/SkinnyBarNotifications';
import { WelcomeCardErrorPage } from '../LoyaltyWelcomeCard/WelcomeCardErrorPage/WelcomeCardErrorPage';
import { WelcomeCardFooter } from '../LoyaltyWelcomeCard/WelcomeCardFooter';
import { DrawerContainer } from '../../common/Drawer/DrawerContainer';
import { OffersCardHeader } from './OffersCardHeader/OffersCardHeader';
import OffersNotificationSection from './OffersNotificationSection/NotificationSection';
import OffersPromotionSection from './OffersPromotionSection/PromotionSection';

/**
 * For all intents and purposes, this component is a copy of LoyaltyWelcomeCard.jsx.
 * The main differences will be the lack of a UserProfileSummary and a different WelcomeCardHeader.
 * I put this in a different folder to adhere to the current folder structure and to protect our
 * future selves in case we need to make changes to the OffersCard that are not applicable to the LoyaltyWelcomeCard.
 * I am open to moving this to the LoyaltyWelcomeCard folder if we feel that is more appropriate.
 */

const { bool, string } = proptypes;

const exportsForTesting = {};

const App = ({ componentId, impressionData, fillHeight }) => {
  const { channel } = useContext(ExperienceContext);
  const {
    isLoading, refreshData, tiles, isAdmin, userDataError, showSweepstakesTile
  } = useContext(WelcomeCardContext);
  const isMobile = channel === 'mobile';

  const { ref, clickID } = useImpression({
    data: impressionData,
  });

  const handleOnClick = () => {
    refreshData();
  };

  const CardComponent = () => {
    const cardClasses = `sui-w-full sui-p-4
      sui-relative sui-border-primary sui-border-solid sui-border-1 sui-rounded-md
      ${fillHeight ? 'sui-flex sui-flex-col' : ''}`;
    return (
      <div className={cardClasses}>
        {/* eslint-disable tailwindcss/no-arbitrary-value */}
        <div
          className={fillHeight ? 'sui-flex sui-flex-col sui-grow' : undefined}
          ref={ref}
          // eslint-disable-next-line react/no-unknown-property
          clickid={clickID}
        >
          <Responsive minWidth={641}>
            <div className="sui-mb-[14px]">
              <OffersCardHeader />
            </div>
          </Responsive>

          {/** mobile header */}
          <Responsive maxWidth={640}>
            <div className="sui-mb-[6px]">
              <OffersCardHeader />
            </div>
          </Responsive>

          <main className={`sui-px-5${fillHeight ? ' sui-grow' : ''}`}>
            <Responsive minWidth={640}>
              <div className="sui-w-1/2 sui-mb-[8px]">{!isMobile && isAdmin && <SpendSummary isOffersCard />}</div>
            </Responsive>
            <Responsive maxWidth={639} minWidth={501}>
              <div className="sui-w-3/5 sui-mb-[8px]">{!isMobile && isAdmin && <SpendSummary isOffersCard />}</div>
            </Responsive>
            <Responsive maxWidth={500}>
              <div className="sui-w-5/6 sui-mb-[8px]">{!isMobile && isAdmin && <SpendSummary isOffersCard />}</div>
            </Responsive>
            <div className="sui-w-full sui-bg-white sui-flex sui-flex-col">
              {isAdmin ? (
                <>
                  <div>
                    <OffersNotificationSection tiles={tiles.NOTIFICATION} />
                  </div>
                  {!showSweepstakesTile && <OffersSkinnyBarNotifications />}
                </>
              ) : (
                <OffersPromotionSection tiles={tiles.PROMOTION} />
              )}
            </div>
          </main>

          <footer className="sui-w-full sui-pt-4 sui-mt-4">
            <WelcomeCardFooter />
            <DrawerContainer />
          </footer>
        </div>
      </div>
    );
  };

  const RenderOffersCard = () => {
    return (
      <div className="sui-grid" data-component="LoyaltyBenefits">
        {!isLoading && !userDataError ? (
          <CardComponent />
        ) : (
          <WelcomeCardErrorPage handleOnClick={handleOnClick} cardLoaded={!isLoading} />
        )}
      </div>
    );
  };

  exportsForTesting.handleOnClick = handleOnClick;
  exportsForTesting.CardComponent = CardComponent;
  exportsForTesting.RenderOffersCard = RenderOffersCard;

  return <RenderOffersCard />;
};

exportsForTesting.App = App;

// fillHeight: stretch to the parent's height (e.g. a grid row); main absorbs the extra space.
const OffersCard = ({ componentId = '', fillHeight = false }) => {
  const impressionData = {
    id: componentId || '',
    name: 'OffersCard',
    component: 'OffersCard',
    position: 1,
    type: 'content',
  };

  return (
    <ComponentProviders componentName="OffersCard">
      <ImpressionProvider data={impressionData}>
        <App componentId={componentId} impressionData={impressionData} fillHeight={fillHeight} />
      </ImpressionProvider>
    </ComponentProviders>
  );
};

OffersCard.propTypes = {
  componentId: string.isRequired,
  fillHeight: bool,
};

OffersCard.displayName = 'OffersCard';

OffersCard.dataModel = LoyaltyWelcomeCardModel;

export { exportsForTesting, OffersCard };